import { create } from "zustand";
import { api, ApiError, setSessionLostHandler, tokens } from "@/lib/api";
import { can, canAny } from "@/lib/permissions";

export interface AdminProfile {
  id: string;
  email?: string | null;
  name?: string | null;
  phone?: string | null;
  role: string;
  avatar_url?: string | null;
  last_login_at?: string | null;
  must_change_password?: boolean;
}

export interface SendOtpResult {
  phone: string;
  formattedPhone?: string;
  otp?: string;
  smsSent?: boolean;
  smsError?: string;
}

export interface VerifyOtpResult {
  token: string;
  refreshToken: string;
  user: AdminProfile;
  isNewUser?: boolean;
  needsOnboarding?: boolean;
}

interface AuthState {
  admin: AdminProfile | null;
  permissions: string[];
  roleKey: string | null;
  status: "loading" | "authenticated" | "anonymous";

  bootstrap: () => Promise<void>;
  sendOtp: (phone: string) => Promise<SendOtpResult>;
  verifyOtp: (phone: string, otp: string) => Promise<VerifyOtpResult>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;

  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  admin: null,
  permissions: [],
  roleKey: null,
  status: "loading",

  async bootstrap() {
    if (!tokens.access() && !tokens.refresh()) {
      set({ status: "anonymous", admin: null, permissions: [], roleKey: null });
      return;
    }
    try {
      const data = await api.get<{ user: AdminProfile }>("/admin/auth/me").catch(() => api.get<{ user: AdminProfile }>("/user/me"));
      if (data?.user && data.user.role === "admin") {
        set({
          admin: data.user,
          permissions: ["*"],
          roleKey: "admin",
          status: "authenticated",
        });
      } else {
        tokens.clear();
        set({ status: "anonymous", admin: null, permissions: [], roleKey: null });
      }
    } catch {
      tokens.clear();
      set({ status: "anonymous", admin: null, permissions: [], roleKey: null });
    }
  },

  async sendOtp(phone: string) {
    const { data, message } = await api.public<SendOtpResult>("/admin/auth/send-otp", {
      phone,
    });
    return { ...data, message } as SendOtpResult & { message?: string };
  },

  async verifyOtp(phone: string, otp: string) {
    const { data } = await api.public<VerifyOtpResult>("/admin/auth/verify-otp", {
      phone,
      otp,
    });

    if (!data.user || data.user.role !== "admin") {
      tokens.clear();
      throw new ApiError(
        403,
        "Access denied: This phone number is not registered as an Admin account.",
      );
    }

    tokens.setSession(data.token, data.refreshToken);
    set({
      admin: data.user,
      permissions: ["*"],
      roleKey: "admin",
      status: "authenticated",
    });

    return data;
  },

  async logout() {
    tokens.clear();
    set({
      admin: null,
      permissions: [],
      roleKey: null,
      status: "anonymous",
    });
  },

  async refreshProfile() {
    try {
      const data = await api.get<{ user: AdminProfile }>("/user/me");
      if (data?.user) {
        set({
          admin: data.user,
          permissions: ["*"],
          roleKey: data.user.role || "admin",
        });
      }
    } catch {
      // Ignored
    }
  },

  can: (permission) => {
    const permissions = get().permissions;
    if (permissions.includes("*") || get().admin?.role === "admin") return true;
    return can(permissions, permission);
  },
  canAny: (permissions) => {
    const granted = get().permissions;
    if (granted.includes("*") || get().admin?.role === "admin") return true;
    return canAny(granted, permissions);
  },
}));

// When a refresh fails mid-session, drop straight to anonymous
setSessionLostHandler(() => {
  useAuthStore.setState({
    admin: null,
    permissions: [],
    roleKey: null,
    status: "anonymous",
  });
});
